import ResourceController from 'controllers/ResourceController';
import labour from './labour';
import crime from './crime';
import charity from './charity';
import trade from './trade';
const debug = require('debug')('party:skills:progressionManager');

let defaultState = {
  work: { ...labour, experience: 0, currentLevelIndex: 0 },
  crime: { ...crime, experience: 0, currentLevelIndex: 0 },
  charity: { ...charity, experience: 0, currentLevelIndex: 0 },
  trade: { ...trade, experience: 0, currentLevelIndex: 0 }
};

const adjustExperienceGain = (path, amount, traits = {}) => {
  let traitMultiplier = 1;
  Object.entries(path.traitMultipliers).forEach(([trait, multiplier]) => {
    const traitValue = traits.getTraitValue(trait) || 0;
    traitMultiplier += traitValue * multiplier;
  });
  return amount * traitMultiplier;
};
const addExperienceToPath = (path, amount, traits) => {
  const adjustedAmount = adjustExperienceGain(path, amount, traits);
  const updatedExperience = path.experience + adjustedAmount;
  const updatedLevelIndex = path.levels.findLastIndex(level => updatedExperience >= level.minExperience);
  // -1 means the experience sits below every threshold on the path, so the floor is the first
  // level. This used to fall back to `levels.length - 1` — the *last* one — which turned any
  // NaN or negative experience into instant promotion to Foreman or Outlaw Legend, and the
  // level-up below would then register that level's dailyBenefits for the rest of the run.
  const currentLevelIndex = updatedLevelIndex === -1 ? 0 : updatedLevelIndex;

  return {
    ...path,
    experience: updatedExperience,
    currentLevelIndex
  };
};

const getCurrentLevel = path => path.levels[path.currentLevelIndex];

const getCurrentLevelIndex = path => path.currentLevelIndex;

const isPathUnlocked = path => path.experience >= path.levels[1].minExperience;

const status = state => {
  return Object.keys(state)
    .map(pathName => {
      if (!isPathUnlocked(state[pathName])) return null;

      const path = state[pathName];
      const currentLevel = getCurrentLevel(path);
      const currentLevelIndex = getCurrentLevelIndex(path);
      const label = `**${path.label}**`;
      const value = currentLevelIndex + 1 === path.levels.length ? 'Max' : `Lvl ${currentLevelIndex}`;

      // Prefix to distinguish from static traits
      return {
        label,
        value: value,
        tooltip: `**${currentLevel.name}** - \n${currentLevel.description}`
      };
    })
    .filter(entry => entry !== null);
};

const createStateManager = initialState => {
  let state = initialState || defaultState;
  let listeners = [];

  return {
    getPath: pathName => state[pathName],
    addExperience: (pathName, amount, traits, context) => {
      let result = null;
      const path = state[pathName];
      if (!path) {
        console.warn(`Unknown path: ${pathName}`);
        return result;
      }
      const oldIndex = path.currentLevelIndex;
      state = {
        ...state,
        [pathName]: addExperienceToPath(path, amount, traits, context)
      };

      const updatedLevelIndex = getCurrentLevelIndex(state[pathName]);
      if (updatedLevelIndex !== oldIndex) {
        const newLevel = state[pathName].levels[updatedLevelIndex];
        result = newLevel.onLevelUp;
        // Stamped with where it came from. createPassiveAction supplies no `type`, and
        // ResourceController.registerAction only de-duplicates on one — so an untyped benefit
        // could only ever be pushed, never replaced, and the same level registering twice
        // (across a restart, or any future re-entry) paid out twice. The key is per level and
        // per benefit rather than per path, because the levels are meant to stack: labour pays
        // 0.1 a day from the Herder rung and 1.5 more from Foreman.
        newLevel.dailyBenefits?.forEach((benefit, index) => {
          ResourceController.instance().registerAction({
            ...benefit,
            type: `${pathName}:${updatedLevelIndex}:${index}`
          });
        });
        debug('LEVEL UP', listeners);
        listeners.forEach(listener => listener(path, newLevel, context));
        debug(`Unlocking event: ${result}`);
      }
      return result;
    },
    getCurrentLevel: pathName => getCurrentLevel(state[pathName]),
    getCurrentLevelIndex: pathName => getCurrentLevelIndex(state[pathName]),
    isPathUnlocked: pathName => isPathUnlocked(state[pathName]),
    totalLevels: () => {
      let total = 0;
      Object.keys(state).forEach(pathName => {
        const path = state[pathName];
        if (isPathUnlocked(path)) {
          total += getCurrentLevelIndex(path);
        }
      });
      return total;
    },
    getHighestUnlockedLevel: () => {
      let highest = 0;
      Object.keys(state).forEach(pathName => {
        const path = state[pathName];
        if (isPathUnlocked(path)) {
          highest = Math.max(highest, getCurrentLevelIndex(path));
        }
      });
      return highest;
    },
    getState: () => state,
    setCallbacks: list => {
      debug('set callbacks', list);
      listeners = list;
    },
    getDailyBenefits: () => {
      let benefitActions = [];
      Object.keys(state).forEach(pathName => {
        const path = state[pathName];
        const currentLevelIndex = getCurrentLevelIndex(path);

        for (let i = 0; i <= currentLevelIndex; i++) {
          const level = path.levels[i];
          if (level.dailyBenefits?.length > 0) {
            benefitActions = benefitActions.concat(level.dailyBenefits);
          }
        }
      });

      return benefitActions;
    },
    status: () => status(state)
  };
};

export default createStateManager;
